import React from "react";

/**
 * Renders long texts (offer, privacy policy, articles) typed or pasted in the
 * admin, with light formatting — no HTML is ever injected:
 *   # Заголовок / ## Подзаголовок          → headings
 *   "1. Общие положения" (short, no period) → section heading
 *   "1.1. Текст пункта…"                    → numbered clause
 *   "- пункт" / "• пункт"                   → bullet list
 *   **жирный**                              → bold
 * Blank line = new paragraph; single line break is kept.
 */
function inline(text: string): React.ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>,
  );
}

export function RichText({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  let list: string[] = [];
  const flushList = () => {
    if (list.length) {
      const items = list;
      blocks.push(
        <ul key={`ul${blocks.length}`}>
          {items.map((li, i) => (
            <li key={i}>{inline(li)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };
  for (const raw of text.replace(/\r\n/g, "\n").split(/\n\s*\n/)) {
    const lines = raw.split("\n").map((l) => l.trimEnd()).filter((l) => l.trim());
    if (!lines.length) continue;
    let para: string[] = [];
    const flushPara = () => {
      if (para.length) {
        const p = para;
        blocks.push(
          <p key={`p${blocks.length}`}>
            {p.map((l, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {inline(l)}
              </React.Fragment>
            ))}
          </p>,
        );
        para = [];
      }
    };
    for (const line of lines) {
      const l = line.trim();
      const bullet = l.match(/^[-•–]\s+(.*)$/);
      if (bullet) {
        flushPara();
        list.push(bullet[1]);
        continue;
      }
      flushList();
      if (l.startsWith("## ")) {
        flushPara();
        blocks.push(<h3 key={`h${blocks.length}`}>{inline(l.slice(3))}</h3>);
      } else if (l.startsWith("# ")) {
        flushPara();
        blocks.push(<h2 key={`h${blocks.length}`}>{inline(l.slice(2))}</h2>);
      } else if (/^\d{1,2}\.\s+\S/.test(l) && l.length <= 90 && !/[.;:,]$/.test(l)) {
        // "1. Общие положения" — a section title in legal documents
        flushPara();
        blocks.push(<h3 key={`h${blocks.length}`}>{inline(l)}</h3>);
      } else if (/^\d{1,2}(\.\d{1,2})+\.?\s/.test(l)) {
        // "1.1. Текст пункта" — each clause its own paragraph with a bold number
        flushPara();
        const m = l.match(/^(\d{1,2}(?:\.\d{1,2})+\.?)\s+(.*)$/)!;
        blocks.push(
          <p key={`c${blocks.length}`} className="clause">
            <strong>{m[1]}</strong> {inline(m[2])}
          </p>,
        );
      } else {
        para.push(l);
      }
    }
    flushPara();
    flushList();
  }
  flushList();
  return <div className="post-body rich-text">{blocks}</div>;
}
